/*
 * Copyright (c) Kalaora 2022.
 * Author: fharold
 *
 * hilo-oauth-manager
 * Modal.tsx
 */

import React from "react";
import ReactDOM from "react-dom";
import HiloReactComponent from "./HiloReactComponent";
import Button from "../element/button/Button";
import Colors from "../../model/enumerations/Colors";
import {LabelAccessor} from "../LabelAccessor";

export interface ModalProps {
    title: LabelAccessor;
    negativeButtonText?: string;
    positiveButtonText?: string;
}

export interface ModalState {
    loading: boolean;
    posBtnDisabled?: boolean;
}

export const DEFAULT_MODAL_CONTAINER_ID = '#modal-container';

export default class Modal<P extends ModalProps, S extends ModalState> extends HiloReactComponent<P, S> {
    public onSave?: Function = undefined;
    protected _positiveButtonText: string;
    protected _negativeButtonText: string;
    private _container?: Element;

    public constructor(props: P, state: S) {
        super(props, state);

        this._positiveButtonText = this.t.modalPositiveBtn;
        this._negativeButtonText = this.t.modalNegativeBtn;
    }

    /**
     * componentDidMount
     */
    public componentDidMount() {
        super.componentDidMount();
        this._container = document.querySelector(DEFAULT_MODAL_CONTAINER_ID) || undefined;
        document.addEventListener('keydown', this.keyDown);
    }

    /**
     * componentWillUnmount
     */
    public componentWillUnmount() {
        super.componentWillUnmount();
        document.removeEventListener('keydown', this.keyDown);
    }

    /**
     * dismiss
     */
    public dismiss = () => {
        if (!this._container) {
            console.warn("Did you forget to call super.componentDidMount ?");
            return;
        }

        ReactDOM.unmountComponentAtNode(this._container);
    };

    /**
     * keyPressed
     */
    public keyDown = (e: KeyboardEvent) => {
        // ESC keycode == 27
        if (e.keyCode !== 27) {
            return;
        }

        this.dismiss();
    };

    /**
     * renderBody
     */
    public renderBody(): JSX.Element {
        return <></>;
    }

    /**
     * render
     */
    public render(): JSX.Element {
        const {loading, posBtnDisabled} = this.state;
        const {title} = this.props;

        return <div className="modal is-active modal-background">
            <div className="modal-card">
                <header className="modal-card-head">
                    <p className="modal-card-title">{title(this.t)}</p>
                </header>

                <section className="modal-card-body">
                    {this.renderBody()}
                </section>

                <footer className="modal-card-foot">
                    {this._positiveButtonText &&
                    <Button color={Colors.blue} onClick={this._saveClicked} disabled={posBtnDisabled} loading={loading}
                            labelAccessor={(t) => this._positiveButtonText?.toString() || t.save}/>}
                    <Button outline={true} color={Colors.blue} onClick={this.dismiss}
                            labelAccessor={(t) => this._negativeButtonText?.toString() || t.modalNegativeBtn}/>
                </footer>
            </div>
        </div>;
    }

    /**
     * _toggleLoading
     */
    protected _toggleLoading() {
        this.setState({loading: !this.state.loading});
    }

    /**
     * _saveClicked
     * @private
     */
    private _saveClicked = () => {
        this.onSave?.();
    };
}